<template>
  <!-- Лоадер загрузки новой карты -->
  <div
    v-if="isMapUploadingStart"
    class="map-upload-loader"
  >
    <div class="map-upload-loader__content">
      <LocalLoader />
      <span class="map-upload-loader__text">{{ t("no_map_view.loader") }}</span>
    </div>
  </div>

  <!-- Существующая карта -->
  <div
    v-else-if="mapFileList.length > 0"
    class="map-wrapper"
  >
    <MapNavbar
      :key="activeMap"
      :map-list="mapFileList"
      :active-map="activeMap"
      @get-project-map-files="getProjectMapFiles"
      @show-create-map-modal="toggleCreateAppModal"
      @update-filter="updateFilter"
      @close-news-list="isNewsListOpened = false"
      @set-active-map="(newMap) => setActiveMap(newMap)"
    />

    <MainMap
      v-if="activeMap"
      :key="activeMap"
      ref="mapComponentRef"
      :filter-key="filterKey"
      :map="activeMap"
      :is-news-list-opened="isNewsListOpened"
      @open-news-list="(val) => openNewsList(val)"
      @update-filter="updateFilter"
    />

    <MapNewsListView
      :key="activeMap"
      :is-news-list-opened="isNewsListOpened && newsList.length > 1"
      :news-list="newsList"
      :city-name="activeNewsListCityName"
      :active-filter-type-option="activeFilterTypeOption"
      @close-news-list="isNewsListOpened = false; mapComponentRef.hideVectorFlower()"
    />

    <BaseNewsCard
      v-if="isNewsListOpened && newsList.length === 1"
      :key="`${activeMap}-${newsList[0]?.id}`"
      :is-news-list-opened="isNewsListOpened"
      :news-item="newsList.length > 0 ? newsList[0] : null"
      :city-name="activeNewsListCityName"
      :active-filter-type-option="activeFilterTypeOption"
      variant="standalone"
      @close="isNewsListOpened = false"
    />
  </div>

  <!-- NoMapView при отсутствие карт. -->
  <NoMapView
    v-show="!isMapUploadingStart && mapFileList.length === 0"
    @get-project-map-files="getProjectMapFiles"
    @set-map-loading="handleSetMapLoading"
    @map-created="handleMapCreated"
  />

  <CreateMapModal
    v-show="isShowMapUploadModal"
    :created-map-list="mapFileList"
    @toggle-create-map-modal="toggleCreateAppModal"
    @set-map-loading="handleSetMapLoading"
    @map-created="handleMapCreated"
    @map-creation-error="handleMapCreationError"
  />
</template>

<script setup>
import MainMap from "@/components/MapPage/MainMap.vue";
import MapNewsListView from "@/components/MapPage/MapNews/MapNewsList.vue";
import MapNavbar from "@/components/MapPage/MapNavbar.vue";
import {useLK} from "@/controllerLK/LKstore";
import { ref, provide, onMounted} from "vue";
import {useRubricsStore} from "@/store/NewsFeed/rubrics";
import NoMapView from "@/components/MapPage/NoMapView.vue";
import { useMapStore } from "@/store/Map/map-store";
import LocalLoader from "@/components/UI/LocalLoader.vue";
import { t } from "@/controllerLK/AvalLocalization";
import CreateMapModal from "@/components/MapPage/CreateMapModal.vue";
import BaseNewsCard from "@/components/MapPage/MapNews/BaseNewsCard.vue";

const mapComponentRef = ref(null);

const mapStore = useMapStore();
const rubricStore = useRubricsStore();
const LKStore = useLK();

const isShowMapUploadModal = ref(false);

const filterKey = ref(0);
provide("filterKey", filterKey);
const newsList = ref([]);
const activeNewsListCityName = ref("");
const isNewsListOpened = ref(false);

const toggleCreateAppModal = () => {
  isShowMapUploadModal.value = !isShowMapUploadModal.value;
};

// Обработчик ошибки создания карты
const handleMapCreationError = () => {
  // Открываем модалку заново при ошибке
  isShowMapUploadModal.value = true;
};

// Функция для сохранения даты с временной меткой
const saveDateFilterWithExpiry = (dateFilter) => {
  const now = new Date();
  const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  const item = {
    value: dateFilter,
    expiry: endOfDay.getTime() // До конца текущего дня
  };
  localStorage.setItem("avalMapActiveDateFilter", JSON.stringify(item));
};

// Функция для получения даты с проверкой срока действия
const getDateFilterWithExpiry = () => {
  const itemStr = localStorage.getItem("avalMapActiveDateFilter");

  if (!itemStr) {
    return null;
  }

  try {
    const item = JSON.parse(itemStr);
    const now = new Date().getTime();

    // Проверяем, не истек ли срок действия
    if (now > item.expiry) {
      // Удаляем устаревшую запись
      localStorage.removeItem("avalMapActiveDateFilter");
      return null;
    }

    return item.value;
  } catch (error) {
    // Если формат данных неверный, удаляем запись
    localStorage.removeItem("avalMapActiveDateFilter");
    return null;
  }
};

// Инициализация фильтров из localStorage
const initializeFilters = async () => {
  // Получаем список рубрик и записываем их в активные
  selectedRubric.value = await rubricStore.getAllRubrics();
  selectedRubric.value = selectedRubric.value.filter((rubric) => rubric.is_fixed);

  // Тип активного фильтра
  const storageActiveFilterType = localStorage.getItem("avalMapActiveFilterType");
  if (storageActiveFilterType) activeFilterTypeOption.value = storageActiveFilterType;

  // Выбранная дата с проверкой срока действия
  const savedDateFilter = getDateFilterWithExpiry();
  if (savedDateFilter) {
    selectedDateFilter.value = savedDateFilter;
  }

  // Важность
  const storageActiveImportanceFilter = localStorage.getItem("avalMapActiveImportanceFilter");
  if (storageActiveImportanceFilter) selectedColorList.value = JSON.parse(storageActiveImportanceFilter);

  // Сантимент
  const storageActiveSentimentFilter = localStorage.getItem("avalMapActiveSentimentFilter");
  if (storageActiveSentimentFilter) selectedSentimentFilter.value = JSON.parse(storageActiveSentimentFilter);
};

onMounted(async () => {
  isMapUploadingStart.value = true;
  await getProjectMapFiles();

  if (mapFileList.value.length > 0) {
    // Инициализируем фильтры
    await initializeFilters();

    // Активная карта
    const storageActiveMapPath = localStorage.getItem("avalActiveMap");

    if (storageActiveMapPath) {
      const storageActiveMap = LKStore.project.map_files.filter((map) => map.map_file_path === storageActiveMapPath);
      if (storageActiveMap.length > 0) {
        activeMap.value = storageActiveMap[0];
      } else {
        activeMap.value = mapFileList.value[0];
        localStorage.setItem("avalActiveMap", activeMap.value.map_file_path);
      }
    } else {
      activeMap.value = mapFileList.value[0];
      localStorage.setItem("avalActiveMap", activeMap.value.map_file_path);
    }
  }
  isMapUploadingStart.value = false;
});

const getProjectMapFiles = async () => {
  mapFileList.value =  await mapStore.getProjectMapFiles(LKStore.project.id);
};

const setActiveMap = async (map) => {
  await getProjectMapFiles();
  console.log("set activemap", map);
  if (!map && mapFileList.value.length > 0) {
    const firstMapInList = mapFileList.value[0];
    localStorage.setItem("avalMapActiveMap", firstMapInList.map_file_path);
    activeMap.value = firstMapInList;
    console.log("set atcive map", activeMap.value, firstMapInList, mapFileList.value);
  } else {
    localStorage.setItem("avalActiveMap", map.map_file_path);
    activeMap.value = map;
  }

  newsList.value = [];
  activeNewsListCityName.value = "";
};

const updateFilter = () => {
  filterKey.value += 1;
  newsList.value = [];
  activeNewsListCityName.value = "";
  isNewsListOpened.value = false;
  updateStorageFilter();
};

const updateStorageFilter = () => {
  localStorage.setItem("avalMapActiveFilterType", activeFilterTypeOption.value);
  saveDateFilterWithExpiry(selectedDateFilter.value);
  localStorage.setItem("avalMapActiveImportanceFilter", JSON.stringify(selectedColorList.value));
  localStorage.setItem("avalMapActiveSentimentFilter", JSON.stringify(selectedSentimentFilter.value));
};

const openNewsList = (newNewsList) => {
  newsList.value = newNewsList.newsList;
  activeNewsListCityName.value = newNewsList.cityName;
  isNewsListOpened.value = true;
};

// Обработчик изменения состояния загрузки
const handleSetMapLoading = (loading) => {
  isMapUploadingStart.value = loading;
};

// Обработчик успешной загрузки карты
const handleMapCreated = async (mapData) => {
  try {
    uploadedMapData.value = mapData;

    // Получаем обновленный список карт
    await getProjectMapFiles();

    // Инициализируем фильтры, если они еще не инициализированы
    if (selectedRubric.value.length === 0) {
      await initializeFilters();
    }

    // Находим загруженную карту в списке и делаем активной
    const uploadedMap = mapFileList.value.find(
      map => map.map_file_path === uploadedMapData.value.map_file_path
    );

    if (uploadedMap) {
      await setActiveMap(uploadedMap);
    } else if (mapFileList.value.length > 0) {
      await setActiveMap(mapFileList.value[0]);
    }
  } catch (error) {
    console.error("Error in handleMapCreated:", error);
  } finally {
    // Скрываем лоадер всегда, даже при ошибке
    isMapUploadingStart.value = false;
    uploadedMapData.value = null;
  }
};

const activeMap = ref(null);
const mapFileList = ref([]);

// Состояние загрузки новой карты на сервер
const isMapUploadingStart = ref(false);
const uploadedMapData = ref(null);

// Фильтры
// Дата
const selectedDateFilter = ref({
  dateFrom: 0,
  dateTo: 0
});
provide("selectedDateFilter", selectedDateFilter);

// Список выбранной важности
const selectedColorList = ref([]);
provide("selectedColorList", selectedColorList);

// Список выбранных сантиментов
const selectedSentimentFilter = ref([]);
provide("selectedSentimentFilter", selectedSentimentFilter);

// Cписок выбранных тем
const selectedTheme = ref([]);
provide("selectedTheme", selectedTheme);

// Список выбранных рубрик
const selectedRubric = ref([]);
provide("selectedRubric", selectedRubric);

// Тип активного фильтра. Importance/Sentiment
const activeFilterTypeOption = ref("importance");
provide("activeFilterTypeOption", activeFilterTypeOption);

</script>

<style scoped lang="scss">
.map-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 91.5vh;
  gap: 20px;
}

.map-upload-loader {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 89.5vh;

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 20px;
  }

  &__text {
    font-size: 16px;
    font-weight: 500;
    line-height: 22px;
    color: var(--purple-purple100);
  }
}
</style>